<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import ReviewForm, { type ReviewFormValues } from './ReviewForm.vue';
import type { ExpertData } from './ExpertDetailsCard.vue';
import { useUpdateAdminReview } from '@/app/composables/reviews/useUpdateAdminReview';
import { useAdminProjectValueRanges } from '@/app/composables/reviews/useAdminProjectValueRanges';

const props = withDefaults(defineProps<{
  reviewId: number;
  expert: ExpertData;
  initialValues: {
    quality: number;
    communication: number;
    timelines: number;
    value_for_money: number;
    review_text: string;
    project_value_range_id: number | null;
    is_anonymous?: boolean;
    hired_on_shopexperts_override?: boolean | null;
  };
  showCloseButton?: boolean;
}>(), {
  showCloseButton: true,
});

const emit = defineEmits<{
  success: [];
  cancel: [];
}>();

const formRef = ref<InstanceType<typeof ReviewForm> | null>(null);
const { updateReview, isUpdating, isSuccess, reset } = useUpdateAdminReview();
const {
  ranges,
  isLoadingRanges,
  rangesError,
  retryRanges,
} = useAdminProjectValueRanges();
const projectValueRangeOptions = computed(() => [
  { label: 'Not specified', value: 'none' },
  ...ranges.value.map((range) => ({ label: range.label, value: String(range.id) })),
]);

const handleSubmit = async (values: ReviewFormValues) => {
  try {
    await updateReview(props.reviewId, {
      quality: values.quality,
      communication: values.communication,
      timelines: values.timelines,
      value_for_money: values.value_for_money,
      review_text: values.review_text,
      project_value_range_id: values.project_value_range_id ?? null,
      is_anonymous: values.is_anonymous,
      hired_on_shopexperts_override: values.hired_on_shopexperts_override,
    });
  } catch (error) {
    console.error('[EditAdminReviewForm] Submit error:', error);
  }
};

watch(isSuccess, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      emit('success');
    }, 1500);
  }
});

const handleCancel = () => {
  emit('cancel');
};

const resetForm = () => {
  formRef.value?.resetForm();
  reset();
};

defineExpose({ resetForm });
</script>

<template>
  <ReviewForm
    ref="formRef"
    :key="reviewId"
    :expert="expert"
    :show-close-button="showCloseButton"
    :is-submitting="isUpdating"
    :is-success="isSuccess"
    :initial-values="{
      ...initialValues,
      hired_on_shopexperts_override: initialValues.hired_on_shopexperts_override ?? false,
    }"
    :show-hired-override-checkbox="true"
    :show-project-value-range-select="true"
    :project-value-range-options="projectValueRangeOptions"
    :is-loading-project-value-ranges="isLoadingRanges"
    :project-value-ranges-failed="Boolean(rangesError)"
    hired-override-label="Override hired at Shopexperts"
    title="Edit **review**"
    description="Update the ratings, project value, review text, privacy, and hired source for this review."
    submit-label="Update Review"
    @submit="handleSubmit"
    @cancel="handleCancel"
    @retry-project-value-ranges="retryRanges"
  />
</template>
